import {Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {HierarchyService} from '../../shared/hierarchy.service';
import {catchError, first, flatMap, map, switchMap, takeUntil} from 'rxjs/operators';
import {
    ECMeasuresTemplateType,
    ECMode,
    EnergyCommunity,
    EntityType,
    POD_ABSORBED_ENERGY_DEVICE,
    POD_AVERAGE_ABSORBED_POWER_DEVICE,
    POD_AVERAGE_INJECTED_POWER_DEVICE,
    POD_INJECTED_ENERGY_DEVICE,
    POD_INSTANT_ABSORBED_POWER_DEVICE,
    POD_INSTANT_INJECTED_POWER_DEVICE,
    PLANT_AVERAGE_PRODUCED_POWER_DEVICE,
    PLANT_INSTANT_PRODUCED_POWER_DEVICE,
    PLANT_PRODUCED_ENERGY_DEVICE,
    Plant
} from 'smag-commons/cfg/hierarchy';
import {EnergyCommunityNetwork} from '../../shared/entities';
import {NetworkService} from '../../shared/network.service';
import {TimeFilterComponent} from '../../shared/time-filter/time-filter.component';
import {SmagApiService} from '../../api/smag-api.service';
import {
    DataFactory,
    DataSources,
    Measure,
    MeasureAggregationWithType,
    MeasureData,
    MeasureDataType,
    MeasureOption,
    MeasureResource,
    MeasuresComponent,
    TIME_RESOLUTIONS,
    TimeAggregationValue,
    TimeResolution
} from './measures-component';
import {downloadBlob, isDefined, safeRatio} from '../../commons/commons';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {ResizeService} from '../../shared/resize.service';
import {
    EntityMeasureAggregationType,
    EntityNetworkType as SmagEntityNetworkType,
    MeasureAggregationDataSource,
    Permission,
    Scope,
    TIME_UNITS,
    TimeAggregationType
} from 'smag-commons/dto/api';
import {can} from '../../shared/permissions';
import {ECNetwork} from '../../shared/ec-resource-tree/ec-resource-tree.component';
import {BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject} from 'rxjs';
import {isConsumer, isProducer, isProsumer} from 'smag-commons/fcn';
import {I18NService} from '../../i18n/i18n.service';
import {MsgBoxService} from '../../shared/msg-box.service';
import {Dialog, DialogService} from '../../shared/dialog/dialog.service';
import {mkMode} from '../../shared/energy-community';
import {AuthService} from 'auth-commons';
import {ChartLine} from '../../shared/line-chart/line-chart.component';
import {ConfigurationService} from '../../shared/configuration.service';
import {mkConsolidatedMonths, YearSummaryConsolidation} from '../../shared/ec-economics';
import {ComputationStatusService} from '../../shared/compute-status.service';
import * as moment from 'moment-timezone';
import {SpinnerData} from '../../shared/spinner/spinner.component';

interface TemplateData {
    type: ECMeasuresTemplateType;
}

export interface DialogData {
    type: ECMeasuresTemplateType;
    resourceId: string;
    resourceName: string;
}

export enum MeasureNames {
    ABSORBED_ENERGY = 'Absorbed energy',
    AVERAGE_ABSORBED_POWER = 'Average absorbed power',
    AVERAGE_INJECTED_POWER = 'Average Effective injected power',
    AVERAGE_PRODUCED_POWER = 'Average produced power',
    CONSUMED_ENERGY = 'Consumed energy',
    GENERATED_ENERGY = 'Generated energy',
    INJECTED_ENERGY = 'Effective injected energy',
    INSTANT_ABSORBED_POWER = 'Instant absorbed power',
    INSTANT_INJECTED_POWER = 'Instant Effective injected power',
    INSTANT_PRODUCED_POWER = 'Instant produced power',
    PRODUCED_ENERGY = 'Produced energy',
    SELF_CONSUMED = 'Self-consumed energy',
    SELF_CONSUMED_RATIO = 'Self-consumed energy ratio',
    SHARED_ENERGY = 'Shared energy',
    SHARED_ENERGY_RATIO = 'Shared energy ratio',
}

const DIALOG_MAX_WIDTH = 1300;
const DIALOG_WIDTH = 700;

@Component({
    selector: 'app-ec-measures',
    templateUrl: './ec-measures.component.html',
    styleUrls: ['./ec-measures.component.scss']
})
export class ECMeasuresComponent extends MeasuresComponent implements OnInit, OnDestroy {

    @ViewChild('timeFilter') protected timeFilterComponent: TimeFilterComponent;
    @ViewChild('uploadMeasures') private uploadMeasuresDialog: TemplateRef<any>;
    @ViewChild('uploadGSEMeasures') private uploadGSEMeasuresDialog: TemplateRef<any>;
    @ViewChild('deleteGSEData') private deleteGSEDataDialog: TemplateRef<any>;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;

    ecNetwork: ECNetwork | null;
    templateData: TemplateData;
    downloadingTemplate: boolean;
    ecMode: ECMode;
    isAdv: boolean;
    showDateWarning: boolean;
    consolidatedMonths: YearSummaryConsolidation[] = [];
    activeMonths: YearSummaryConsolidation[] = []; // all months since community activation, both consolidated and not consolidated
    timeZone: string;
    loadingEconomicsData = false;
    uploadGSEMeasuresDialogRef: Dialog<any, number>;

    // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — data sources hidden via legend click, keyed by
    // measure name. A legend entry may map to more than one source (e.g. monitored + hand-loaded on a CACER).
    private hiddenOrigins = new Map<string, Set<string>>();
    // the template binds [data]="mkChartLines(...)",
    // so this runs on every change-detection cycle. Cache the result per measure and recompute only when the
    // underlying measure data reference or the legend visibility changes, returning a stable array reference so
    // app-line-chart is not re-rendered needlessly (ETT-1161).
    private chartLinesCache = new Map<string, { measData: MeasureData[]; hidden: string; lines: ChartLine[] }>();
    // @PoweredByAi(date="2026-07-27", model="claude-opus-5") — ETT-1484: the per-source lines *before* the legend
    // visibility is applied. The legend asks which data sources the chart actually carries (see hasOrigin) and that
    // answer must not depend on what is currently hidden, or hiding an entry would remove the entry itself. Cached
    // for the same reason as chartLinesCache: it is read from the template on every change-detection cycle and it
    // walks every point of the series.
    private baseLinesCache = new Map<string, { measData: MeasureData[]; lines: ChartLine[] }>();

    // exported in HTML
    isDefined = isDefined;
    MeasureDataType = MeasureDataType;
    dialogData: DialogData;
    MeasureAggregationDataSource = MeasureAggregationDataSource;
    EntityType = EntityType;

    constructor(@Inject(APP_CONFIG_TOKEN) readonly cfg: AppConfig,
                readonly resizeService: ResizeService,
                readonly hierarchyService: HierarchyService,
                readonly networkService: NetworkService,
                readonly apiService: SmagApiService,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private readonly dialogService: DialogService,
                private readonly authService: AuthService,
                configurationService: ConfigurationService,
                private readonly computationStatusService: ComputationStatusService) {
        super(cfg, resizeService, hierarchyService, networkService, apiService, configurationService);
        this.ecNetwork = null;
        this.templateData = null;
        this.dialogData = null;
        this.downloadingTemplate = false;
        this.ecMode = null;
        this.dialogService = dialogService;
        this.showDateWarning = false;
    }

    ngOnInit() {
        this.onInit();
        this.isAdv = this.authService.profile.scopes?.includes(Scope.ADVANCED);

        this.hierarchyService.selection.pipe(
            switchMap(res => res ?
                this.apiService.getEntity(res.id).pipe( // ask API for resource details
                    map(resData => ({
                        ...resData,
                        id: res.id,
                        name: res.name,
                        permissions: res.permissions || []
                    } as MeasureResource))
                ) :
                of(null as MeasureResource)),
            switchMap(res => {
                if (res?.type === EntityType.ENERGY_COMMUNITY) {
                    const activationDate = (res as unknown as EnergyCommunity)?.activation_date || null;
                    this.showDateWarning = activationDate ? Date.now() < activationDate : true;
                }
                if (!res || res.type === EntityType.ENERGY_COMMUNITY) {
                    return of([res?.id, res] as [string, MeasureResource]);
                } else {
                    this.showDateWarning = false;
                    return this.getResourceCommunityId(res.id).pipe( // something that *could* be part of an energy community is selected
                        map(networkId => (networkId ? [networkId, res] : [null, null]) as [string, MeasureResource]));
                }
            }),
            takeUntil(this.destroyed)
        ).subscribe(([networkId, resource]) => {
            this.updateSelectedResource(resource);
            this.hiddenOrigins.clear(); // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — reset legend toggles on resource change (ETT-1161)
            this.chartLinesCache.clear(); // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — drop memoized chart lines on resource change (ETT-1161)
            this.baseLinesCache.clear(); // @PoweredByAi(date="2026-07-27", model="claude-opus-5") — ETT-1484
            this.loadNetwork(networkId, resource?.id)
                .then(() => {
                    this.updateMeasureOptions(this.mkMeasureOptions());
                    this.onResourceSelected();
                    if (!this.ecNetwork) {
                        return;
                    }
                    this.configurationService.timeConfig.pipe(first(),
                        flatMap(timeCfg => mkConsolidatedMonths(this.ecNetwork.network.root.id,
                            this.ecNetwork.network.rootEntity.activation_date,
                            timeCfg.time_zone,
                            this.apiService)
                            .then(consolidatedMonths => {
                                this.timeZone = timeCfg.time_zone;
                                return consolidatedMonths;
                            })
                        ),
                    ).subscribe(consolidatedMonths => {
                        this.activeMonths = consolidatedMonths;
                        this.consolidatedMonths = consolidatedMonths.filter(consolidatedMonth => consolidatedMonth.consolidated);
                    });
                });
        });

        this.gseDataUpdated.subscribe(year => {
            this.loadingEconomicsData = true;
            if (!this.ecNetwork) {
                return;
            }
            const rootId = this.ecNetwork.network.root.id;
            const activationDate = this.ecNetwork.network.rootEntity.activation_date;
            const years = year === null ? this.getYears(activationDate) : [year];
            const statusChecks = years.map(y => this.computationStatusService.checkComputationStatus(rootId, y));
            forkJoin(statusChecks)
                .pipe(takeUntil(this.destroyed))
                .subscribe(() => mkConsolidatedMonths(rootId, activationDate, this.timeZone, this.apiService)
                    .then(consolidatedMonths => {
                        this.activeMonths = consolidatedMonths;
                        this.consolidatedMonths = consolidatedMonths.filter(consolidatedMonth => consolidatedMonth.consolidated);
                        this.loadingEconomicsData = false;
                    }));
        });
    }

    ngOnDestroy() {
        this.onDestroy();
    }

    get canReadCommunity(): boolean {
        return can(this.ecNetwork.network.root.permissions, Permission.READ);
    }

    get canUpdateCommunity(): boolean {
        return can(this.ecNetwork.network.root.permissions, Permission.UPDATE);
    }

    get hasNoProductionPod(): boolean {
        return this.resource.type === EntityType.PLANT ? !(this.resource as unknown as Plant).production_pod : false;
    }

    onDataUpload() {
        this.uploadGSEMeasuresDialogRef.close();
        this.gseDataUpdated.next(null);
    }

    // Given measure data and data type, check if file or meter measures are to show because fallback
    checkFallbacks(measureData: MeasureData[], dataType: MeasureDataType) {
        const fileAllFallback = measureData.find((md) => md.subName === MeasureAggregationDataSource.FILE && md.data.filter(([_, v]) => v !== null).length === md.fallbackTimes.size);
        const meterAllFallback = dataType === MeasureDataType.MONITORED ? measureData.find((md) => md.subName === MeasureAggregationDataSource.METER && md.data.filter(([_, v]) => v !== null).length === md.fallbackTimes.size) : true;
        return [!!fileAllFallback, !!meterAllFallback];
    }

    // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — distinct stroke width per data source (ETT-1161):
    // hand-loaded measures are 2px, every other source stays at 3px.
    private setStrokeWidth(measureData: MeasureData) {
        if (measureData.subName === MeasureAggregationDataSource.FILE) {
            return 2;
        } else {
            return 3;
        }
    }

    // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — distinct opacity per data source (ETT-1161):
    // hand-loaded measures use 50% of the standard colour, every other source is fully opaque.
    private setOpacity(measureData: MeasureData) {
        if (measureData.subName === MeasureAggregationDataSource.FILE) {
            return 0.5;
        } else {
            return 1;
        }
    }

    private setColor(measureData: MeasureData) {
        if (measureData.subName === MeasureAggregationDataSource.GSE) {
            return measureData.consolidation_color;
        } else {
            return measureData.color;
        }
    }

    get isEcSelected() {
        return this.resource?.type === EntityType.ENERGY_COMMUNITY;
    }

    setTypeData(md: MeasureData) {
        if (md.subName === MeasureAggregationDataSource.METER) {
            return ` (${this.i18n.translate('Monitored')})`;
        }
        if (md.subName === MeasureAggregationDataSource.FILE) {
            return ` (${this.i18n.translate('Hand-loaded')})`;
        }
    }

    hasGSE(measData: MeasureData[]) {
        return (measData.find(md => md.subName === MeasureAggregationDataSource.GSE));
    }

    /**
     * Whether the chart of the given measure carries data coming from the given data source, ignoring whatever the
     * legend currently hides.
     *
     * At community level the legend has to name the origin the data actually has, not the one the community *could*
     * have according to its mode: a CER served entirely by statistical profiles has no solid monitored/hand-loaded
     * curve to name, and a CER with no statistical data at all has no dashed curve to name.
     * @PoweredByAi(date="2026-07-27", model="claude-opus-5", prompt="A CER whose PODs only have statistical profiles labelled its measures as monitored/hand-loaded (ETT-1484)")
     */
    hasOrigin(measData: MeasureData[], measName: string, origin: string): boolean {
        return this.mkBaseChartLines(measData, measName).some(line => line.subName === origin);
    }

    mkChartLines(measData: MeasureData[], measName?: string): ChartLine[] {
        const hiddenKey = this.hiddenOriginsKey(measName);
        const cached = measName ? this.chartLinesCache.get(measName) : null;
        if (cached && cached.measData === measData && cached.hidden === hiddenKey) {
            return cached.lines;
        }
        // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — apply legend-driven visibility by data source.
        const lines = this.applyOriginVisibility(measName, this.mkBaseChartLines(measData, measName));
        if (measName) {
            this.chartLinesCache.set(measName, {measData, hidden: hiddenKey, lines});
        }
        return lines;
    }

    /**
     * The chart lines of a measure, one per data source, as they are before the legend visibility is applied.
     * @PoweredByAi(date="2026-07-27", model="claude-opus-5") — ETT-1484: split off mkChartLines so that both the
     * chart and the legend read the same per-source lines, and so that walking every point of the series happens
     * once per data refresh instead of once per change-detection cycle.
     */
    private mkBaseChartLines(measData: MeasureData[], measName?: string): ChartLine[] {
        const cached = measName ? this.baseLinesCache.get(measName) : null;
        if (cached && cached.measData === measData) {
            return cached.lines;
        }
        // From measure data, generate chart lines array
        let filtered: ChartLine[] = measData.map(md => ({
            data: md.data,
            // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — the statistical profile is a fully dashed
            // line (constant pattern), every other source is solid (ETT-1161).
            style: {
                color: this.setColor(md),
                strokeWidth: this.setStrokeWidth(md),
                opacity: this.setOpacity(md),
                dashed: md.subName === MeasureAggregationDataSource.PROFILE
            },
            fallbackTimes: md.subName !== MeasureAggregationDataSource.PROFILE ? md.fallbackTimes : createSetFromPositionZero(md.data), // if profile, use data as fallback to generate dashed line in chart
            subName: md.subName
        })).filter((md) => !!md.data.length); // Remove lines with no data
        // Keep the monitored (METER) and hand-loaded (FILE) series as their own lines, each shown only
        // where it has real data, and render the statistical profile (PROFILE) as its own dashed line in
        // the gaps where no real series has data. Keeping every data source on a separate line
        // is what lets each one carry its own style — e.g. statistical stays at full opacity / 3px even
        // when it fills the gaps with a 50%-opacity hand-loaded series — and be toggled independently of the legend.
        // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8")
        // @PoweredByAi(date="2026-07-27", model="claude-opus-5") — ETT-1484: the community-level CASCADE series is
        // split the same way, so that on a CER too every data source is a line of its own.
        filtered = splitCascadeByOrigin(restrictProfileToGaps(filtered))
            .filter(line => line.data.some(([_, v]) => v !== null)) // drop series left without visible data
            // Monitored/hand-loaded are always solid: once the statistical fallback has been split off into
            // the PROFILE line, drop the fallback markers so no dashing is computed on the real series.
            .map(line => line.style.dashed ? line : {...line, fallbackTimes: undefined});
        if (measName) {
            this.baseLinesCache.set(measName, {measData, lines: filtered});
        }
        return filtered;
    }

    // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — stable serialization of the hidden data sources
    // for a measure, used as the chart-lines cache key (ETT-1161).
    private hiddenOriginsKey(measName?: string): string {
        const hidden = measName ? this.hiddenOrigins.get(measName) : null;
        return hidden ? [...hidden].sort().join(',') : '';
    }

    // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — ETT-1161
    // Apply legend visibility to the chart lines. After restrictProfileToGaps and splitCascadeByOrigin every data
    // source is its own line (the statistical fallback has been split off into the PROFILE line), so visibility
    // is a simple per-line drop keyed by subName:
    //   - PROFILE -> Statistical, GSE -> Consolidated, METER/FILE -> Monitored/Hand-loaded,
    //     CASCADE -> Monitored/hand-loaded at community level (@PoweredByAi(date="2026-07-27", model="claude-opus-5") — ETT-1484).
    // When no origin is hidden the lines are returned untouched, so the default rendering is unchanged.
    private applyOriginVisibility(measName: string, lines: ChartLine[]): ChartLine[] {
        const hidden = measName ? this.hiddenOrigins.get(measName) : null;
        if (!hidden || !hidden.size) {
            return lines;
        }
        return lines.filter(line => !hidden.has(line.subName));
    }

    // Find first valid uom
    getUom(measData: MeasureData[]) {
        return measData.length && measData.find((md) => !!md.uom).uom;
    }

    // Check if MeasureData has multiline data
    isMultilineChart(measData: MeasureData[]) {
        return measData.length > 1;
    }

    // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — ETT-1161: legend-driven show/hide of curves by data source.
    // Whether a given data source is currently hidden for a measure.
    isOriginHidden(measName: string, origin: string): boolean {
        return !!measName && !!origin && !!this.hiddenOrigins.get(measName)?.has(origin);
    }

    // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8")
    // Whether a legend entry (one or more sources) is currently hidden. Considered hidden only when *all* its sources are.
    isLegendEntryHidden(measName: string, ...origins: string[]): boolean {
        return origins.length > 0 && origins.every(origin => this.isOriginHidden(measName, origin));
    }

    /**
     * Whether the chart of a measure carries any series at all, once the series left without values have been
     * dropped (see mkBaseChartLines). Data points alone are not enough: a monitored series whose every value was
     * served by a statistical profile keeps its points but draws no line, and a measure left with nothing to draw
     * has no legend entry either, so it gets the plain "no data" state rather than an empty legend over a blank
     * chart. Only a measure that does carry series can reach the "all series hidden" state.
     * @PoweredByAi(date="2026-07-30", model="claude-opus-5", prompt="ETT-1484: a measure whose series were all dropped showed an empty legend and a blank chart, with neither message")
     */
    hasAnySeries(measData: MeasureData[], measName: string): boolean {
        return !!this.mkBaseChartLines(measData, measName).length;
    }

    // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8")
    // Toggle the visibility of a legend entry (one or more sources) for a measure.
    toggleOrigin(measName: string, ...origins: string[]): void {
        if (!measName || !origins.length) {
            return;
        }
        const hidden = this.hiddenOrigins.get(measName) || new Set<string>();
        const show = origins.every(origin => hidden.has(origin)); // currently fully hidden -> show again
        origins.forEach(origin => show ? hidden.delete(origin) : hidden.add(origin));
        this.hiddenOrigins.set(measName, hidden);
    }

    // Given a single measure, check if there are some value to show in legend
    hasLegend(md: MeasureData, measData: MeasureData[], dataType: MeasureDataType) {
        const checkValues = md.sumValue != null && true || md.meanValue != null && true || md.maxValue != null && true || md.minValue != null && true;
        if (this.resource?.type === EntityType.ENERGY_COMMUNITY) {
            return checkValues;
        } else {
            const [fileAllFallback, meterAllFallback] = this.checkFallbacks(measData, dataType);
            const showProfile = (md.subName === MeasureAggregationDataSource.PROFILE || md.subName === MeasureAggregationDataSource.GSE) && (fileAllFallback && meterAllFallback) && md.data.filter(([_, v]) => v !== null).length !== md.fallbackTimes.size;
            const notShowProfile = md.subName !== MeasureAggregationDataSource.PROFILE && !(fileAllFallback && meterAllFallback) && md.data.filter(([_, v]) => v !== null).length !== md.fallbackTimes.size;
            return (showProfile || notShowProfile) && checkValues;
        }
    }

    exportMeasuresToExcel() {
        let {fromTime, toTime} = this.timeFilter.range();
        fromTime = roundTime(fromTime, this.timeResolution.resolution);
        toTime = roundTime(toTime, this.timeResolution.resolution);
        this.downloadingTemplate = true;
        this.apiService.exportMeasuresToExcel(this.resource.id,
            this.templateData.type,
            fromTime,
            `${Math.round((toTime - fromTime) / 60000)} ${TIME_UNITS.MINUTES}`,
            `${this.timeResolution.resolution} ${TIME_UNITS.MINUTES}`)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
                this.downloadingTemplate = false;
                this.msgBoxService.info(this.i18n.translate('File downloaded!'));
            }, error => {
                this.downloadingTemplate = false;
                this.msgBoxService.error(this.i18n.translate('An error occurred while fetching the template'), error);
            });
    }

    protected adjustTimeResolutionForMeasureDataTypes(dataTypes: MeasureDataType[]) {
        if (!this.resource) {
            return;
        }
        // get the finest resolution allowed by (currently selected) measures
        let finestRes: TimeResolution = null;
        if (this.resource.type === EntityType.ENERGY_COMMUNITY) {
            // for energy communities, the finest allowed resolution depends on economics
            finestRes = TIME_RESOLUTIONS.find(tr => tr.resolution === 60);
        } else {
            super.adjustTimeResolutionForMeasureDataTypes(dataTypes);
        }
        if (finestRes) {
            const timeResolutions = this.timeResolutions.filter(tr => tr.resolution >= finestRes.resolution);
            if (!!timeResolutions.length) {
                this.timeResolutions = timeResolutions;
                // if currently selected resolution is "incompatible" with the finest one allowed,
                // select the finest available resolution
                if (!this.timeResolutions.find(tr => tr.resolution === this.timeResolution.resolution)) {
                    this.timeResolution = this.timeResolutions.reduce((p, c) => c.resolution < p.resolution ? c : p);
                }
            }
        }
    }

    protected mkMeasureOptions(): MeasureOption[] {
        switch (this.resource?.type) {
            case EntityType.ENERGY_COMMUNITY:
                return this.mkEnergyCommunityMeasureOptions();
            case EntityType.POD:
                return this.mkPodMeasureOptions();
            case EntityType.PLANT:
                return this.mkPlantMeasureOptions();
            case EntityType.ACCUMULATOR:
                return this.mkStorageMeasureOptions();
            default:
                return [];
        }
    }

    protected updateMeasuresType(measures: Measure[]) {
        // for energy community, we have a much simpler criterion...
        if (this.resource?.type === EntityType.ENERGY_COMMUNITY) {
            if (this.ecMode === ECMode.ADVANCED) {
                this.measuresType = MeasureDataType.MONITORED;
            } else if (this.ecMode === ECMode.BASE) {
                this.measuresType = MeasureDataType.STATISTICAL;
            } else if (this.ecMode === ECMode.HYBRID) {
                this.measuresType = MeasureDataType.HYBRID;
            }
            return;
        }
        super.updateMeasuresType(measures);
    }

    private onResourceSelected() {
        this.templateData = this.mkTemplateData();
        this.dialogData = this.mkDialogData();
    }

    private getResourceCommunityId(resourceId: string): Observable<string | null> {
        // employ easy "local" caching (resource is already part of current network)
        if (!!this.ecNetwork && resourceId in this.ecNetwork.network.entities) {
            return of(this.ecNetwork.network.root.id);
        }
        return this.apiService.getNetworkOfResource(resourceId).pipe(
            map(networkInfo => {
                if (networkInfo?.type !== SmagEntityNetworkType.ENERGY_COMMUNITY) {
                    throw new Error(`Bad type ${networkInfo.type} for network ${networkInfo.network_id}`);
                }
                return networkInfo.network_id;
            }),
            catchError(err => {
                console.error(`Resource ${resourceId} is not part of an energy community`, err);
                return of(null);
            })
        );
    }

    private loadNetwork(networkId: string, selectedResourceId?: string): Promise<void> {
        // `networkId` is assumed to refer to an energy community
        if (!networkId) {
            this.ecNetwork = null;
            return Promise.resolve();
        }
        // employ easy "local" caching (network already loaded)
        if (!!this.ecNetwork && networkId === this.ecNetwork.network.root.id) {
            this.ecNetwork = {
                network: this.ecNetwork.network,
                selectedResourceId
            };
            // `ecMode` is left unchanged
            return Promise.resolve();
        }
        this.ecNetwork = null; // triggers spinner
        return forkJoin([
            this.networkService.buildECNetwork(networkId),
            this.apiService.communityPodsStatistics(networkId).pipe(
                map(stats => mkMode(stats))
            )
        ]).toPromise()
            .then(([network, ecMode]) => {
                this.ecNetwork = {
                    network: network as EnergyCommunityNetwork,
                    selectedResourceId
                };
                this.ecMode = ecMode;
            });
    }

    private processECMeasure(name: MeasureNames,
                             dataType: MeasureDataType,
                             measureAggregationWithTypes: MeasureAggregationWithType[],
                             measureCollapsedAggregationWithTypes: MeasureAggregationWithType[],
                             dataSources: DataSources[],
                             uom: string,
                             color: string,
                             consolidationColor: string): MeasureOption {
        return {
            name,
            dataType,
            dataFactory: this.internalMkAggregationDataFactory(measureAggregationWithTypes, measureCollapsedAggregationWithTypes, dataSources, uom, name, color, consolidationColor)
        };
    }

    // Make measures for EC community
    private mkEnergyCommunityMeasureOptions(): MeasureOption[] {
        const communityEnergyGeneration = [{
            measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION,
            timeAggregation: TimeAggregationType.SUM
        }];
        const communityEnergySelfConsumption = [{
            measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION,
            timeAggregation: TimeAggregationType.SUM
        }];
        const communityEnergyInjection = [{
            measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION,
            timeAggregation: TimeAggregationType.SUM
        }];
        const communityEnergyAbsorption = [{
            measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_ABSORPTION,
            timeAggregation: TimeAggregationType.SUM
        }];
        const communitySharedEnergy = [{
            measureAggregation: EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY,
            timeAggregation: TimeAggregationType.SUM
        }];
        return [
            this.processECMeasure(MeasureNames.GENERATED_ENERGY, null, communityEnergyGeneration, communityEnergyGeneration, [
                    {dataSource: MeasureAggregationDataSource.CASCADE}
                ], 'kWh', this.cfg.shared.aggregationColors[EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION],
                this.cfg.shared.aggregationColorsConsolidation[EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION]),
            this.processECMeasure(MeasureNames.SELF_CONSUMED, null, communityEnergySelfConsumption, communityEnergySelfConsumption, [
                    {dataSource: MeasureAggregationDataSource.CASCADE}
                ], 'kWh', this.cfg.shared.aggregationColors[EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION],
                this.cfg.shared.aggregationColorsConsolidation[EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION]),
            this.processECMeasure(MeasureNames.SELF_CONSUMED_RATIO, null, [
                {
                    measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION,
                    timeAggregation: TimeAggregationType.MEAN
                },
                {
                    measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION,
                    timeAggregation: TimeAggregationType.MEAN
                }
            ], [
                {
                    measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION,
                    timeAggregation: TimeAggregationType.SUM
                },
                {
                    measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION,
                    timeAggregation: TimeAggregationType.SUM
                }
            ], [
                {dataSource: MeasureAggregationDataSource.CASCADE}
            ], '%', this.cfg.shared.colors.selfConsumedRatio, this.cfg.shared.colors.consolidatedSelfConsumedRatio),
            this.processECMeasure(MeasureNames.INJECTED_ENERGY, null, communityEnergyInjection, communityEnergyInjection, [
                    {dataSource: MeasureAggregationDataSource.GSE},
                    {dataSource: MeasureAggregationDataSource.CASCADE}
                ], 'kWh', this.cfg.shared.aggregationColors[EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION],
                this.cfg.shared.aggregationColorsConsolidation[EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION]),
            this.processECMeasure(MeasureNames.ABSORBED_ENERGY, null, communityEnergyAbsorption, communityEnergyAbsorption, [
                    {dataSource: MeasureAggregationDataSource.GSE},
                    {dataSource: MeasureAggregationDataSource.CASCADE},
                ], 'kWh', this.cfg.shared.aggregationColors[EntityMeasureAggregationType.COMMUNITY_ENERGY_ABSORPTION],
                this.cfg.shared.aggregationColorsConsolidation[EntityMeasureAggregationType.COMMUNITY_ENERGY_ABSORPTION]),
            this.processECMeasure(MeasureNames.SHARED_ENERGY, null, communitySharedEnergy, communitySharedEnergy, [
                    {dataSource: MeasureAggregationDataSource.GSE},
                    {dataSource: MeasureAggregationDataSource.CASCADE},
                ], 'kWh', this.cfg.shared.aggregationColors[EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY],
                this.cfg.shared.aggregationColorsConsolidation[EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY]),
            this.processECMeasure(MeasureNames.SHARED_ENERGY_RATIO, null, [
                    {
                        measureAggregation: EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY_RATIO,
                        timeAggregation: TimeAggregationType.MEAN,
                        coefficient: 100
                    }
                ], [
                    {
                        measureAggregation: EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY,
                        timeAggregation: TimeAggregationType.SUM
                    },
                    {
                        measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION,
                        timeAggregation: TimeAggregationType.SUM
                    }
                ], [
                    {dataSource: MeasureAggregationDataSource.GSE},
                    {dataSource: MeasureAggregationDataSource.CASCADE},
                ], '%', this.cfg.shared.aggregationColors[EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY_RATIO],
                this.cfg.shared.aggregationColorsConsolidation[EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY_RATIO]),
            this.processECMeasure(MeasureNames.CONSUMED_ENERGY, null, [
                    {
                        measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_ABSORPTION,
                        timeAggregation: TimeAggregationType.SUM
                    },
                    {
                        measureAggregation: EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION,
                        timeAggregation: TimeAggregationType.SUM
                    }
                ], [], [
                    {dataSource: MeasureAggregationDataSource.CASCADE},
                ], 'kWh', this.cfg.shared.aggregationColors[EntityMeasureAggregationType.COMMUNITY_ENERGY_CONSUMPTION],
                this.cfg.shared.aggregationColorsConsolidation[EntityMeasureAggregationType.COMMUNITY_ENERGY_CONSUMPTION])
        ];
    }

    private generateDataSource(dataType: MeasureDataType) {
        const dataSources: DataSources[] = [
            {dataSource: MeasureAggregationDataSource.GSE}
        ];
        if (dataType === MeasureDataType.MONITORED) {
            dataSources.push({dataSource: MeasureAggregationDataSource.METER});
        }
        dataSources.push(
            {dataSource: MeasureAggregationDataSource.FILE},
            {dataSource: MeasureAggregationDataSource.PROFILE}
        );
        return dataSources;
    }

    private processPodPlantMeasure(measureType: string,
                                   measureName: MeasureNames,
                                   aggregationType: TimeAggregationType,
                                   uom: string,
                                   color: string,
                                   consolidationColor: string,
                                   coefficient?: number,
                                   computeTotal?: boolean,
                                   computeMean?: boolean,
                                   computeMin?: boolean,
                                   computeMax?: boolean): MeasureOption {
        const dataType = this.mkMeasureDataType(measureType);
        if ((isDefined(dataType) && dataType === MeasureDataType.MONITORED) ||
            (isDefined(dataType) && dataType !== MeasureDataType.MONITORED &&
                measureType !== POD_AVERAGE_ABSORBED_POWER_DEVICE &&
                measureType !== POD_AVERAGE_INJECTED_POWER_DEVICE &&
                measureType !== POD_INSTANT_INJECTED_POWER_DEVICE &&
                measureType !== PLANT_AVERAGE_PRODUCED_POWER_DEVICE)) {
            const dataSources = this.generateDataSource(dataType);
            return {
                name: measureName,
                dataType,
                dataFactory: this.internalMkDeviceDataFactory(measureType, aggregationType, dataSources, color, consolidationColor, uom, coefficient, computeTotal, computeMean, computeMin, computeMax),
            };
        }
    }

    private mkPodMeasureOptions(): MeasureOption[] {
        const options: MeasureOption[] = [];
        const pod = this.ecNetwork.network.pods[this.resource.id];
        if (isConsumer(pod) || isProsumer(pod)) {
            options.push(this.processPodPlantMeasure(POD_ABSORBED_ENERGY_DEVICE, MeasureNames.ABSORBED_ENERGY, TimeAggregationType.SUM, 'kWh', this.cfg.shared.colors.absorbedPower, this.cfg.shared.colors.consolidatedAbsorbed, null, true));
            options.push(this.processPodPlantMeasure(POD_AVERAGE_ABSORBED_POWER_DEVICE, MeasureNames.AVERAGE_ABSORBED_POWER, TimeAggregationType.MEAN, 'kW', this.cfg.shared.colors.absorbedPower, this.cfg.shared.colors.consolidatedAbsorbed, null, false, true, true, true));
            options.push(this.processPodPlantMeasure(POD_INSTANT_ABSORBED_POWER_DEVICE, MeasureNames.INSTANT_ABSORBED_POWER, TimeAggregationType.MEAN, 'kW', this.cfg.shared.colors.absorbedPower, this.cfg.shared.colors.consolidatedAbsorbed, null, false, true, true, true));
        }
        if (isProducer(pod) || isProsumer(pod)) {
            options.push(this.processPodPlantMeasure(POD_INJECTED_ENERGY_DEVICE, MeasureNames.INJECTED_ENERGY, TimeAggregationType.SUM, 'kWh', this.cfg.shared.colors.injectedPower, this.cfg.shared.colors.consolidatedInjected, null, true));
            options.push(this.processPodPlantMeasure(POD_AVERAGE_INJECTED_POWER_DEVICE, MeasureNames.AVERAGE_INJECTED_POWER, TimeAggregationType.MEAN, 'kW', this.cfg.shared.colors.injectedPower, this.cfg.shared.colors.consolidatedInjected, null, false, true, true, true));
            options.push(this.processPodPlantMeasure(POD_INSTANT_INJECTED_POWER_DEVICE, MeasureNames.INSTANT_INJECTED_POWER, TimeAggregationType.MEAN, 'kW', this.cfg.shared.colors.injectedPower, this.cfg.shared.colors.consolidatedInjected, null, false, true, true, true));
        }
        if (isProsumer(pod)) {
            const dataType = this.mkMeasureDataType(POD_INJECTED_ENERGY_DEVICE);
            if (isDefined(dataType)) {
                const dataSourcesSelfConsumed = this.generateDataSource(dataType);
                const dataSourcesSelfConsumedRatio = this.generateDataSource(dataType);
                const dataSourcesConsumedEnergy = this.generateDataSource(dataType);
                options.push(
                    {
                        name: MeasureNames.SELF_CONSUMED,
                        dataType,
                        dataFactory: this.internalMkProsumerPodEnergy(MeasureNames.SELF_CONSUMED, dataSourcesSelfConsumed, this.cfg.shared.colors.selfConsumedEnergy, this.cfg.shared.colors.consolidatedSelfConsumed)
                    },
                    {
                        name: MeasureNames.SELF_CONSUMED_RATIO,
                        dataType,
                        dataFactory: this.internalMkProsumerPodEnergy(MeasureNames.SELF_CONSUMED_RATIO, dataSourcesSelfConsumedRatio, this.cfg.shared.colors.selfConsumedRatio, this.cfg.shared.colors.consolidatedSelfConsumedRatio)
                    },
                    {
                        name: MeasureNames.CONSUMED_ENERGY,
                        dataType,
                        dataFactory: this.internalMkProsumerPodEnergy(MeasureNames.CONSUMED_ENERGY, dataSourcesConsumedEnergy, this.cfg.shared.colors.consumedPower, this.cfg.shared.colors.consolidatedConsumed)
                    }
                );
            }
        }
        return options.filter(opt => !!opt);
    }

    private mkPlantMeasureOptions(): MeasureOption[] {
        const options: MeasureOption[] = [
            this.processPodPlantMeasure(PLANT_PRODUCED_ENERGY_DEVICE, MeasureNames.PRODUCED_ENERGY, TimeAggregationType.SUM, 'kWh', this.cfg.shared.colors.generatedPower, this.cfg.shared.colors.consolidatedGenerated, null, true, false, false),
            this.processPodPlantMeasure(PLANT_AVERAGE_PRODUCED_POWER_DEVICE, MeasureNames.AVERAGE_PRODUCED_POWER, TimeAggregationType.MEAN, 'kW', this.cfg.shared.colors.generatedPower, this.cfg.shared.colors.consolidatedGenerated, null, false, true, true, true),
            this.processPodPlantMeasure(PLANT_INSTANT_PRODUCED_POWER_DEVICE, MeasureNames.INSTANT_PRODUCED_POWER, TimeAggregationType.MEAN, 'kW', this.cfg.shared.colors.generatedPower, this.cfg.shared.colors.consolidatedGenerated, null, false, true, true, true)
        ];
        return options.filter(opt => !!opt);
    }

    private mkStorageMeasureOptions(): MeasureOption[] {
        // NOTE: nothing currently supported for accumulators
        return [];
    }

    private mkTemplateData(): TemplateData {
        if (!this.resource) {
            return null;
        }
        const data: TemplateData = {
            type: null
        };
        if (this.resource.type === EntityType.ENERGY_COMMUNITY) {
            data.type = ECMeasuresTemplateType.ENERGY_COMMUNITY;
        } else {
            data.type = this.setDataType();
        }
        return data;
    }

    mkDialogData(): DialogData {
        if (!this.resource) {
            return null;
        }
        const data: DialogData = {
            type: null,
            resourceId: this.resource.id,
            resourceName: this.resource.name
        };
        data.type = this.setDataType();
        return data;
    }

    get showUploadMeasuresButton(): boolean {
        return this.dialogData?.type === ECMeasuresTemplateType.PLANT ||
            this.dialogData?.type === ECMeasuresTemplateType.PRODUCER ||
            this.dialogData?.type === ECMeasuresTemplateType.CONSUMER ||
            this.dialogData?.type === ECMeasuresTemplateType.PROSUMER;

    }

    get showUploadGSEMeasuresButton(): boolean {
        return this.dialogData?.type === ECMeasuresTemplateType.ENERGY_COMMUNITY;
    }

    onclickUploadMeasures() {
        this.dialogService.open<any, number>({
            template: this.uploadMeasuresDialog,
            context: this.dialogData,
            title: this.i18n.translate('Upload Measures for ') + this.dialogData.resourceName
        }, {
            width: DIALOG_MAX_WIDTH
        }).closed.then();

    }

    onclickUploadGSEMeasures() {
        this.uploadGSEMeasuresDialogRef = this.dialogService.open<any, number>({
            template: this.uploadGSEMeasuresDialog,
            title: this.i18n.translate('Upload an Excel file from GSE with consolidated data')
        }, {
            width: DIALOG_MAX_WIDTH
        });
    }

    onDeleteGSEData() {
        this.dialogService.open<any, { year: number, month: number }>({
            template: this.deleteGSEDataDialog,
            title: this.i18n.translate('Delete consolidated data'),
            context: {}
        }, {
            width: DIALOG_MAX_WIDTH
        }).closed.then(data => {
            if (!isDefined(data) || !isDefined(data.year) || !isDefined(data.month)) {
                return;
            }
            const spinner = this.openSpinner('Deleting consolidated data...');
            this.apiService.deleteGseData(this.ecNetwork.network.root.id, data.year, data.month + 1) // back-end months span from 1 to 12
                .then(() => {
                    spinner.context.next({
                        loading: false,
                        loadingMessage: this.i18n.translate('Data deleted correctly'),
                    });
                    this.gseDataUpdated.next(data.year);
                })
                .catch(() => {
                    spinner.context.next({
                        loading: false,
                        loadingMessage: this.i18n.translate('An error occurred, please retry later'),
                        abort: true
                    });
                });
        });
    }

    private setDataType(): ECMeasuresTemplateType {
        let type;
        if (this.resource.type === EntityType.POD) {
            const pod = this.ecNetwork.network.pods[this.resource.id];
            if (isProsumer(pod)) {
                type = ECMeasuresTemplateType.PROSUMER;
            } else if (isProducer(pod)) {
                type = ECMeasuresTemplateType.PRODUCER;
            } else {
                type = ECMeasuresTemplateType.CONSUMER;
            }
        } else if (this.resource.type === EntityType.PLANT) {
            type = ECMeasuresTemplateType.PLANT;
        } else if (this.resource.type === EntityType.ENERGY_COMMUNITY) {
            type = ECMeasuresTemplateType.ENERGY_COMMUNITY;
        }
        return type;
    }

    private internalMkDeviceDataFactory(deviceKey: string,
                                        timeAggregation: TimeAggregationType,
                                        dataSources: DataSources[],
                                        color: string,
                                        consolidationColor: string,
                                        defaultUom?: string,
                                        coefficient?: number,
                                        computeTotal?: boolean,
                                        computeMean?: boolean,
                                        computeMin?: boolean,
                                        computeMax?: boolean): DataFactory {
        return (rangeRefreshObs, timeRes) => combineLatest([
            rangeRefreshObs,
            this.loadDeviceUom(deviceKey, defaultUom)
        ]).pipe(
            switchMap(([timeRange, uom]) => {
                const forkJoinObservables = dataSources.map(dataSourceObj => {
                    const {dataSource} = dataSourceObj;
                    return forkJoin([
                        this.loadDeviceData(deviceKey, timeAggregation, dataSource, coefficient, timeRange, timeRes),
                        computeTotal ? this.loadCollapsedDeviceData(deviceKey, TimeAggregationType.SUM, dataSource, timeRange) : of(null as number),
                        computeMean ? this.loadCollapsedDeviceData(deviceKey, TimeAggregationType.MEAN, dataSource, timeRange) : of(null as number)
                    ]).pipe(
                        map(([data, total, mean]) => {
                            if (noData(data.data)) {
                                return this.returnMeasureData([], dataSource, data.fallbackTimes, uom, color, consolidationColor, null, null, null, null);
                            } else {
                                const minVal = computeMin ? safeMin(data.data) : null;
                                const maxVal = computeMax ? safeMax(data.data) : null;
                                return this.returnMeasureData(data.data, dataSource, data.fallbackTimes, uom, color, consolidationColor, maxVal, minVal, total, mean);
                            }
                        })
                    );
                });
                return forkJoin(forkJoinObservables);
            })
        );
    }

    private returnMeasureData(data: [number, number][], subName: string, fallbackTimes: Set<number>, uom: string, color: string, consolidationColor: string, maxValue: number, minValue: number, sumValue: number, meanValue: number): MeasureData {
        return {
            data,
            subName,
            fallbackTimes,
            uom,
            color,
            consolidation_color: consolidationColor,
            maxValue,
            minValue,
            sumValue,
            meanValue
        };
    }

    private internalMkAggregationDataFactory(measureAggregationWithTypes: MeasureAggregationWithType[],
                                             measureCollapsedAggregationWithTypes: MeasureAggregationWithType[],
                                             dataSources: DataSources[],
                                             uom: string,
                                             measure: MeasureNames,
                                             color: string,
                                             consolidationColor: string): DataFactory {
        return (rangeRefreshObs, timeRes) => rangeRefreshObs.pipe(
            switchMap(timeRange => {
                // Create an array of forkJoin observables for each dataSource
                const forkJoinObservables = dataSources.map(dataSourceObj => {
                    const {dataSource} = dataSourceObj;
                    const loadAggregationDataArray: ((dataSource: any, timeRange: any) => Observable<MeasureData>)[] = measureAggregationWithTypes.map(measureAggregationWithType => {
                        return (dataSourceParam, timeRangeParam) => this.loadAggregationData(measureAggregationWithType.measureAggregation, measureAggregationWithType.timeAggregation, dataSourceParam, measureAggregationWithType.coefficient, timeRangeParam, timeRes);
                    });
                    const loadCollapsedAggregationDataArray: ((dataSource, timeRange) => Observable<number>)[] = measureCollapsedAggregationWithTypes.map(measureAggregationWithType => {
                        return (dataSourceParam, timeRangeParam) => this.loadCollapsedAggregationData(measureAggregationWithType.measureAggregation, measureAggregationWithType.timeAggregation, dataSourceParam, timeRangeParam);
                    });
                    return forkJoin([
                        ...loadAggregationDataArray.map(fn => fn(dataSource, timeRange)),
                        ...loadCollapsedAggregationDataArray.map(fn => fn(dataSource, timeRange)),
                    ]).pipe(
                        map((result) => {
                            // Get measures list
                            const mdList = loadAggregationDataArray.map((_, i) => result[i] as MeasureData);
                            // Get number list (possible min, max, sum, mean values)
                            const numberList: TimeAggregationValue[] = [];
                            for (let i = loadAggregationDataArray.length, j = 0; i < loadCollapsedAggregationDataArray.length + loadAggregationDataArray.length; i++, j++) {
                                numberList.push({
                                    timeAggregation: measureCollapsedAggregationWithTypes[j].timeAggregation,
                                    value: result[i] as number
                                });
                            }
                            const getData = (): [number, number][] => {
                                if (mdList.some(md => noData(md.data))) {
                                    if (measure === MeasureNames.CONSUMED_ENERGY) {
                                        return mdList[0].data;
                                    }
                                    return [];
                                }
                                if (mdList.length === 1) {
                                    return mdList[0].data;
                                }
                                const [meas1, meas2] = mdList;
                                if (measure === MeasureNames.CONSUMED_ENERGY) {
                                    return combineArray(meas1.data, meas2.data, safeSum);
                                }
                                if (measure === MeasureNames.SELF_CONSUMED_RATIO) {
                                    return computeRatioForArray(meas1.data, meas2.data);
                                }
                                return [];
                            };
                            // Get max, min, sum values based on measure
                            const getValues = (): [number, number, number] => {
                                if (measure === MeasureNames.CONSUMED_ENERGY) {
                                    const consumed = combineArray(mdList[0].data, mdList[1].data, safeSum);
                                    const sum = consumed.map(([_, v]) => v).reduce((a, b) => a + b, 0);
                                    return [null, null, sum];
                                }
                                if (mdList.some(md => noData(md.data))) {
                                    return [null, null, null];
                                }
                                if (mdList.length === 1) {
                                    const data = mdList[0];
                                    if (numberList.length > 1) {
                                        if (measure === MeasureNames.SHARED_ENERGY_RATIO) {
                                            const [n1, n2] = numberList.map(tav => tav.value);
                                            const ratio = safeRatio(n1, n2);
                                            const commonData = data.data;
                                            return [safeMax(commonData), safeMin(commonData), isDefined(ratio) ? ratio * 100 : null];
                                        }
                                    } else {
                                        const max = numberList.find(tav => tav.timeAggregation === TimeAggregationType.MAX)?.value;
                                        const min = numberList.find(tav => tav.timeAggregation === TimeAggregationType.MIN)?.value;
                                        const sum = numberList.find(tav => tav.timeAggregation === TimeAggregationType.SUM)?.value;
                                        return [max, min, sum];
                                    }
                                } else {
                                    if (numberList.length > 1) {
                                        if (measure === MeasureNames.SELF_CONSUMED_RATIO) {
                                            const [n1, n2] = numberList.map(tav => tav.value);
                                            const ratio = safeRatio(n1, n2);
                                            const commonData = computeRatioForArray(mdList[0].data, mdList[1].data);
                                            return [safeMax(commonData), safeMin(commonData), isDefined(ratio) ? ratio * 100 : null];
                                        }
                                    }
                                }
                                return [null, null, null];
                            };
                            // Get all data to return measure
                            const [maxVal, minVal, sumVal] = getValues();
                            const mainData = noData(getData()) ? [] : getData();
                            // @PoweredByAi(date="2026-07-27", model="claude-opus-5") — ETT-1484: a point of a measure
                            // computed out of several aggregations is statistical as soon as any of its operands is.
                            const fallback = unionFallbackTimes(mdList);
                            return this.returnMeasureData(mainData, dataSource, fallback, uom, color, consolidationColor, maxVal, minVal, sumVal, null);
                        })
                    );
                });
                // Combine all forkJoin observables into a single observable array
                return forkJoin(forkJoinObservables);
            })
        );
    }

    /**
     * calculate pod self consumed energy
     */
    private internalMkProsumerPodEnergy(ecMeasures: MeasureNames, dataSources: DataSources[], color: string, consolidationColor: string): DataFactory {
        return (rangeRefreshObs, timeRes) => rangeRefreshObs.pipe(
            switchMap(timeRange => {
                const forkJoinObservables = dataSources.map(dataSourceObj => {
                    const {dataSource} = dataSourceObj;
                    const loadDevice = ecMeasures === MeasureNames.CONSUMED_ENERGY
                        ? [POD_ABSORBED_ENERGY_DEVICE, POD_INJECTED_ENERGY_DEVICE]
                        : [POD_INJECTED_ENERGY_DEVICE];
                    const loadDevices = this.getPlants().map((plantId) => ({
                        id: plantId,
                        device: PLANT_PRODUCED_ENERGY_DEVICE
                    }));
                    const deviceObservables = loadDevice.map(device =>
                        this.loadDeviceData(device, TimeAggregationType.SUM, dataSource, null, timeRange, timeRes).pipe(
                            map(data => mapData(data, 'kWh'))
                        )
                    );
                    const loadDevicesObservables = loadDevices.map(plantDevice =>
                        this.loadDeviceData(plantDevice.device, TimeAggregationType.SUM, dataSource, null, timeRange, timeRes, plantDevice.id).pipe(
                            map(data => mapData(data, 'kWh'))
                        )
                    );
                    return forkJoin([...deviceObservables, ...loadDevicesObservables])
                        .pipe(
                            map(mds => {
                                const noDataFlag = mds.some(md => noData(md.data));
                                if (noDataFlag) {
                                    const emptyData = ecMeasures !== MeasureNames.SELF_CONSUMED_RATIO ? [] : mds[0].data;
                                    const uom = ecMeasures !== MeasureNames.SELF_CONSUMED_RATIO ? mds[0].uom : '%';
                                    return this.returnMeasureData(emptyData, dataSource, mds[0].fallbackTimes, uom, color, consolidationColor, null, null, null, null);
                                } else {
                                    switch (ecMeasures) {
                                        case MeasureNames.SELF_CONSUMED_RATIO: {
                                            const produced = computeProduced(mds.slice(1));
                                            const selfConsumed = computeSelfConsumed(mds);
                                            const totalSelfConsumed = selfConsumed.data.reduce((total, data) => total + data[1], 0);
                                            const totalProduced = produced.reduce((total, data) => total + data[1], 0);
                                            const ratioData = computeRatioForArray(selfConsumed.data, produced);
                                            return this.returnMeasureData(ratioData, dataSource, selfConsumed.fallbackTimes, '%', color, consolidationColor, safeMax(ratioData), safeMin(ratioData), safeRatio(totalSelfConsumed, totalProduced) * 100, null);
                                        }
                                        case MeasureNames.SELF_CONSUMED: {
                                            const md = computeSelfConsumed(mds);
                                            const sumVal = md.data.reduce((sum, data) => sum + data[1], 0);
                                            return this.returnMeasureData(md.data, dataSource, md.fallbackTimes, md.uom, color, consolidationColor, null, null, sumVal, null);
                                        }
                                        case MeasureNames.CONSUMED_ENERGY: {
                                            const selfConsumed = computeSelfConsumed(mds.slice(1));
                                            const consumed = combineArray(mds[0].data, selfConsumed.data, safeSum);
                                            const sumVal = consumed.reduce((total, data) => data[1] < 0 ? total : total + data[1], 0);
                                            return this.returnMeasureData(consumed, dataSource, mds[0].fallbackTimes, mds[0].uom, color, consolidationColor, null, null, sumVal, null);
                                        }
                                        default:
                                            return null;
                                    }
                                }
                            })
                        );
                });
                return forkJoin(forkJoinObservables);
            })
        );
    }

    /**
     * return child plants of current pod
     * @private
     */
    private getPlants(): string[] {
        const resourceId = this.resource.id;
        return Object.keys(this.ecNetwork.network.pods[resourceId].plants);
    }

    private getYears(activationDate: number): number[] {
        const startYear = moment.tz(activationDate, this.timeZone).year();
        const currentYear = moment.tz(this.timeZone).year();
        return Array.from({length: currentYear - startYear + 1}, (_, i) => startYear + i);
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DIALOG_WIDTH,
            hideCloseButton: false
        });
    }
}

function roundTime(time: number, res: number): number {
    // `time` assumed to be in milliseconds
    // `res` assumed to be in minutes
    return time - (time % (res * 60000));
}

function noData(data: [number, number][]): boolean {
    return data.every(([_, d]) => !isDefined(d));
}

function mapData(data: MeasureData, uom?: string): MeasureData {
    return {
        data: noData(data.data) ? [] : data.data,
        fallbackTimes: data.fallbackTimes,
        uom
    };
}


/**
 * compute the ratio for each value of item in two arrays with same size
 * @param a
 * @param b
 */
function computeRatioForArray(a: [number, number][], b: [number, number][]): [number, number][] | null {
    if ((a.length !== b.length)) {
        return [];
    }
    const ret = new Array<[number, number]>(a.length);
    for (let i = 0; i < a.length; i++) {
        const sr = safeRatio(a[i][1], b[i][1]);
        if (isDefined(sr)) {
            ret[i] = [a[i][0], sr * 100];
        } else {
            ret[i] = null;
        }
    }
    return ret.filter(d => !!d);
}

/**
 * search max value in an array of data
 * @param data
 */
function safeMax(data: [number, number][]): number {
    if (!data || data.length === 0) {
        return null;
    } else {
        return Math.max(0, Math.max(...data.map(v => v[1])));
    }
}

/**
 * search min value in an array of data
 * @param data
 */
function safeMin(data: [number, number][]): number {
    if (!data || data.length === 0) {
        return null;
    } else {
        return Math.max(0, Math.min(...data.map(v => v[1])));
    }
}

/**
 * compute self-consumed energy "sum(produced children pv/wind energy) - injected"
 * @param mds measure data
 */
function computeSelfConsumed(mds: MeasureData[]) {
    if (mds.length <= 1) {
        return mds[0];
    }
    const injected = mds[0]; // shared energy
    const produced = computeProduced(mds.slice(1));
    const selfConsumed = clearNegative(combineArray(produced, injected.data, safeSub));
    return {
        data: selfConsumed,
        fallbackTimes: injected.fallbackTimes,
        uom: injected.uom
    };
}

/**
 * sum produced energy for given pod measures
 */
function computeProduced(measures: MeasureData[]): [number, number][] {
    return measures.map(data => data.data)
        // sum each measure in array
        .reduce((a, b) => combineArray(a, b, safeSum));
}

/**
 * sum two arrays
 * @param a
 * @param b
 * @param operation
 */
function combineArray(a: [number, number][], b: [number, number][], operation: (a: number, b: number) => number): [number, number][] | null {
    if (!a || !b || (a.length !== b.length)) {
        console.error('Array size mismatch');
        return [];
    }
    const ret = new Array<[number, number]>(a.length);
    for (let i = 0; i < a.length; i++) {
        ret[i] = [a[i][0], operation(a[i][1], b[i][1])];
    }
    return ret;
}

export function safeSum(value1: number, value2: number): number | null {
    if (isDefined(value1) || isDefined(value2)) {
        return (value1 || 0) + (value2 || 0);
    } else {
        return null;
    }
}

export function safeSub(value1: number, value2: number): number | null {
    if (isDefined(value1) || isDefined(value2)) {
        return (value1 || 0) - (value2 || 0);
    } else {
        return null;
    }
}

/**
 * replace negative value with 0
 * @param src
 */
export function clearNegative(src: [number, number][]): [number, number][] | null {
    return src.map(([t, v]) => [t, v < 0 ? 0 : v]);
}

function createSetFromPositionZero(arr: [number, number][]): Set<number> {
    return new Set(arr.map(tuple => tuple[0]));
}

/**
 * Split the community-level CASCADE series into one line per data source, the way the measures below community
 * level already are.
 *
 * A community measure is requested as a single `cascade` series that carries monitored, hand-loaded *and*
 * statistical data at once, and the only signal about the origin of a point is its fallback marker: a fallback
 * timestamp was served by a statistical profile, anything else by a smart meter or a hand-loaded file. Splitting
 * it in two — the real values on the solid CASCADE line, the profile values on a dashed PROFILE line — is what
 * makes a CER served purely by statistical profiles read as statistical instead of as monitored/hand-loaded, and
 * it puts the community chart on the same footing as the others: every origin carries its own style, is named by
 * its own legend entry and is hidden by dropping its line. The dashed line uses the constant dash pattern
 * (style.dashed), so no per-segment dash length is computed on a line broken by nulls (see mkDashArray).
 *
 * Each statistical stretch is anchored to the real points bounding it, exactly as restrictProfileToGaps does
 * below community level, so the dashed line touches the solid one instead of leaving a one-interval hole. The
 * dashed line is appended last so it is drawn on top, again as below community level. Lines of any other data
 * source are returned untouched.
 * @PoweredByAi(date="2026-07-27", model="claude-opus-5", prompt="A CER whose PODs only have statistical profiles rendered its measures as a solid monitored/hand-loaded line (ETT-1484)")
 */
export function splitCascadeByOrigin(lines: ChartLine[]): ChartLine[] {
    const cascade = lines.find(line => line.subName === MeasureAggregationDataSource.CASCADE);
    if (!cascade) {
        return lines;
    }
    const isStatistical = (t: number) => cascade.fallbackTimes?.has(t) === true;
    // a real point bordering a statistical stretch is kept on the dashed line as well, to anchor it
    const anchorsStatistical = (i: number) => (i > 0 && isStatistical(cascade.data[i - 1][0]))
        || (i < cascade.data.length - 1 && isStatistical(cascade.data[i + 1][0]));
    const real: ChartLine = {
        ...cascade,
        data: cascade.data.map(([t, v]) => [t, isStatistical(t) ? null : v] as [number, number]),
        fallbackTimes: undefined
    };
    const statistical: ChartLine = {
        ...cascade,
        data: cascade.data.map(([t, v], i) => [t, isStatistical(t) || anchorsStatistical(i) ? v : null] as [number, number]),
        fallbackTimes: undefined,
        style: {...cascade.style, dashed: true},
        subName: MeasureAggregationDataSource.PROFILE
    };
    return lines.map(line => line === cascade ? real : line).concat([statistical]);
}

/**
 * The union of the fallback markers of the given series.
 *
 * A measure computed out of more than one aggregation (consumed energy = absorption + self-consumption, the
 * self-consumption ratio) was served by a statistical profile on a given timestamp as soon as *any* of its
 * operands was, so the markers of every operand have to be merged: keeping only the first operand's markers
 * draws a value partly estimated on statistical profiles as if it were entirely monitored.
 * @PoweredByAi(date="2026-07-27", model="claude-opus-5", prompt="Only the first aggregation's fallback times survived, so consumed energy and the self-consumption ratio could read as monitored on statistical data (ETT-1484)")
 */
export function unionFallbackTimes(mdList: MeasureData[]): Set<number> {
    return (mdList || []).reduce((union, md) => {
        md?.fallbackTimes?.forEach(t => union.add(t));
        return union;
    }, new Set<number>());
}

/**
 * Keep the monitored (METER) and hand-loaded (FILE) series as their own coexisting lines, showing each
 * only where it has real (non-fallback) data, and show the statistical profile (PROFILE) only on the
 * timestamps where neither real series has data. This removes the redundant profile segments that used
 * to "complete" each series (and overlap the other one) while keeping both real series visible.
 *
 * To avoid a visual break where a gap meets a real series, each profile gap is anchored to the real
 * points that bound it: the bounding real value is plugged back into the profile line so the dashed
 * profile path touches the neighbouring monitored/hand-loaded line instead of leaving a hole. GSE and
 * any other line are returned untouched.
 * @PoweredByAi(date="2026-06-24", model="claude-opus-4-5", prompt="Keep monitored and hand-loaded as separate visible lines, show the statistical profile only where both real series are missing, and anchor the profile to bounding real points so there is no break (ETT-1100)")
 */
export function restrictProfileToGaps(lines: ChartLine[]): ChartLine[] {
    // Real (non-null, non-fallback) values keyed by timestamp on the monitored/hand-loaded series.
    const realValues = new Map<number, number>();
    const collectRealValues = (subName: string) => {
        const line = lines.find((l) => l.subName === subName);
        line?.data.forEach(([t, v]) => {
            if (v !== null && !line.fallbackTimes?.has(t) && !realValues.has(t)) {
                realValues.set(t, v);
            }
        });
    };
    collectRealValues(MeasureAggregationDataSource.METER);
    collectRealValues(MeasureAggregationDataSource.FILE);
    const isGap = (t: number) => !realValues.has(t);
    return lines.map((line) => {
        if (line.subName === MeasureAggregationDataSource.METER || line.subName === MeasureAggregationDataSource.FILE) {
            // Drop the fallback (profile) fills so each series only spans the range where it has real data.
            return {...line, data: line.data.map(([t, v]) => [t, line.fallbackTimes?.has(t) ? null : v] as [number, number])};
        }
        if (line.subName === MeasureAggregationDataSource.PROFILE) {
            // Keep the profile value only inside the gaps (where neither real series has data).
            const data = line.data.map(([t, v]) => [t, isGap(t) ? v : null] as [number, number]);
            // Anchor every gap to its bounding real points so the dashed profile connects to the
            // neighboring monitored/hand-loaded line instead of leaving a break.
            for (let i = 0; i < data.length; i++) {
                const [t] = data[i];
                if (!isGap(t) && ((i > 0 && isGap(data[i - 1][0])) || (i < data.length - 1 && isGap(data[i + 1][0])))) {
                    data[i] = [t, realValues.get(t)];
                }
            }
            // No fallbackTimes recompute: the profile line is rendered with style.dashed (constant dash
            // pattern), so its dashing no longer depends on fallbackTimes (ETT-1161).
            return {...line, data};
        }
        return line;
    });
}

